import React, { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import { useDebounce } from 'use-debounce'
import { CgAttachment } from 'react-icons/cg'
import { PiArrowBendUpLeft } from 'react-icons/pi'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/UI/BaseDropdown/dropdown-base'
import { useModalDepth } from 'components/UI/Modal/ModalPrimitive'
import { getDropdownZIndex } from 'styles/z-index'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import useApiService from 'services/api'
import CreatableSelect from 'components/FormUtils/CreatableSelect'
import { TextInput } from 'components/FormUtilsV2/TextInput/text-input'
import { StyledEditorRHF } from 'components/Editor/EditorRHF'
import {
  AttachmentList,
  Attachment,
  isNewAttachment,
} from 'components/Attachments/AttachmentList'
import {
  ALLOWED_EMAIL_ATTACHMENT_TYPES,
  MAX_EMAIL_ATTACHMENT_SIZE,
  EMAIL_ATTACHMENT_WARNING_THRESHOLD,
  getAcceptString,
} from 'constants/fileTypes'
import { useFileAttachments } from 'hooks/useFileAttachments'
import { cn } from 'components/UI/cn'
import { useAuth } from 'context/authentication/useAuth'
import {
  EmailContact,
  EmailMessage,
  EmailThread,
} from 'models/emailIntegration'
import { CATEGORY_BADGE_CLASS, DealBadge } from './DealBadge'

export type ComposeMode = 'reply' | 'reply_all' | 'forward' | 'compose'

/** Payload passed from ComposePanel to parent for optimistic send. */
export interface SendPayload {
  optimisticMessage: EmailMessage
  formData: FormData
  fromId: number
}

/** Handler for creating free-text email options in CreatableSelect. */
const createEmailOption = async (inputValue: string) => ({
  label: inputValue,
  value: inputValue,
})

interface ComposePanelProps {
  mode: ComposeMode
  thread?: EmailThread
  replyToMessage?: EmailMessage
  integrationEmail?: string
  onSend: (payload: SendPayload) => void
  onDiscard: () => void
  onModeChange?: (mode: ComposeMode) => void
  dealIdentifier?: string | null
  dealName?: string | null
  companyName?: string
  /** When true, renders without its own container border/padding (parent provides the chrome). */
  standalone?: boolean
}

const composeSchema = z.object({
  to: z.array(z.string().email()).min(1, 'At least one recipient is required'),
  cc: z.array(z.string().email()).optional(),
  bcc: z.array(z.string().email()).optional(),
  from_id: z
    .number({ message: 'Please select a from email' })
    .min(1, 'Please select a from email'),
  subject: z.string().min(1, 'Subject is required'),
  body: z.string().min(1, 'Message body is required'),
})

type ComposeFormValues = z.infer<typeof composeSchema>

/** Filter out the current user's email from a recipient list. */
function filterSelf(
  contacts: EmailContact[],
  selfEmail?: string
): EmailContact[] {
  if (!selfEmail) return contacts
  return contacts.filter(
    (c) => c.email.toLowerCase() !== selfEmail.toLowerCase()
  )
}

/** Build the default "To" recipients based on compose mode. */
function getDefaultTo(
  mode: ComposeMode,
  replyToMessage?: EmailMessage,
  integrationEmail?: string
): string[] {
  if (mode === 'compose' || mode === 'forward') return []

  const sender = replyToMessage?.sender || []
  const senderEmails = filterSelf(sender, integrationEmail).map((c) => c.email)

  // When replying to your own outbound message, address the original recipients
  // instead of yourself (filterSelf would leave an empty list).
  const fallbackTo = filterSelf(replyToMessage?.to || [], integrationEmail).map(
    (c) => c.email
  )

  if (mode === 'reply') {
    return senderEmails.length > 0 ? senderEmails : fallbackTo
  }

  // reply_all: original sender + original To recipients (minus self)
  // Original Cc recipients go to getDefaultCc
  return [...new Set([...senderEmails, ...fallbackTo])]
}

/** Build the default "Cc" recipients based on compose mode. */
function getDefaultCc(
  mode: ComposeMode,
  replyToMessage?: EmailMessage,
  integrationEmail?: string
): string[] {
  if (mode !== 'reply_all') return []
  return filterSelf(replyToMessage?.cc || [], integrationEmail).map(
    (c) => c.email
  )
}

/** Build default subject — no stacking of Re:/Fwd: per RFC 5322. */
function getDefaultSubject(
  mode: ComposeMode,
  thread?: EmailThread,
  replyToMessage?: EmailMessage
): string {
  const originalSubject = replyToMessage?.subject || thread?.subject || ''

  if (mode === 'compose') return ''

  if (mode === 'forward') {
    // Don't stack Fwd:, but preserve any existing Re: prefix
    if (/^Fwd:/i.test(originalSubject)) return originalSubject
    return `Fwd: ${originalSubject}`
  }

  // reply / reply_all — don't stack Re:
  if (/^Re:/i.test(originalSubject)) return originalSubject
  return `Re: ${originalSubject}`
}

/** Format an email contact list as "Name <email>, ..." for display in quoted headers. */
function formatContactList(contacts: EmailContact[] | null): string {
  if (!contacts?.length) return ''
  return contacts
    .map((c) =>
      c.name ? `${c.name} &lt;${c.email}&gt;` : `&lt;${c.email}&gt;`
    )
    .join(', ')
}

/** Build default body (quoted original for forward, Gmail-style header). */
function getDefaultBody(
  mode: ComposeMode,
  replyToMessage?: EmailMessage
): string {
  if (mode !== 'forward' || !replyToMessage?.body) return ''

  const from = formatContactList(replyToMessage.sender)
  const date = replyToMessage.date
    ? new Date(replyToMessage.date).toLocaleString()
    : ''
  const to = formatContactList(replyToMessage.to)
  const cc = formatContactList(replyToMessage.cc)

  const headerLines = [
    `From: ${from || 'Unknown'}`,
    `Date: ${date}`,
    `Subject: ${replyToMessage.subject || ''}`,
    `To: ${to}`,
  ]
  if (cc) headerLines.push(`Cc: ${cc}`)

  return `<br/><br/><div style="border-left:3px solid #ccc;padding-left:12px;color:#555">
<p>---------- Forwarded message ---------</p>
<p>${headerLines.join('<br/>')}</p>
<br/>
${replyToMessage.body}
</div>`
}

/** Thin alias so we can spread shared props into To/Cc/Bcc recipient fields. */
const RecipientSelect = CreatableSelect

/** Whether the compose mode is inline within a thread (not standalone). */
function isInlineMode(mode: ComposeMode): boolean {
  return mode === 'reply' || mode === 'reply_all' || mode === 'forward'
}

/** Compose panel for reply, reply all, forward, and new compose. */
export function ComposePanel({
  mode,
  thread,
  replyToMessage,
  integrationEmail,
  onSend,
  onDiscard,
  onModeChange,
  dealIdentifier,
  dealName,
  companyName,
  standalone,
}: ComposePanelProps) {
  const api = useApiService()
  const { user } = useAuth()
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const [showCc, setShowCc] = useState(() => {
    if (mode === 'reply_all') {
      const cc = getDefaultCc(mode, replyToMessage, integrationEmail)
      return cc.length > 0
    }
    return false
  })
  const [showBcc, setShowBcc] = useState(false)
  const inline = isInlineMode(mode)
  const modalDepth = useModalDepth()
  const zIndex = React.useMemo(
    () => getDropdownZIndex(modalDepth),
    [modalDepth]
  )

  const {
    fileInputRef,
    openFileSelect,
    handleFileSelect,
    createRemoveHandler,
  } = useFileAttachments({
    allowedTypes: ALLOWED_EMAIL_ATTACHMENT_TYPES,
    maxFileSize: MAX_EMAIL_ATTACHMENT_SIZE,
    maxTotalSize: MAX_EMAIL_ATTACHMENT_SIZE,
    onAdd: (newAttachments) =>
      setAttachments((prev) => [...prev, ...newAttachments]),
  })

  const { data: emailIntegrationOptions } =
    api.emailIntegration.useGetEmailIntegrationOptions()

  const defaultTo = useMemo(
    () => getDefaultTo(mode, replyToMessage, integrationEmail),
    [mode, replyToMessage, integrationEmail]
  )
  const defaultCc = useMemo(
    () => getDefaultCc(mode, replyToMessage, integrationEmail),
    [mode, replyToMessage, integrationEmail]
  )
  const defaultSubject = useMemo(
    () => getDefaultSubject(mode, thread, replyToMessage),
    [mode, thread, replyToMessage]
  )
  const defaultBody = useMemo(
    () => getDefaultBody(mode, replyToMessage),
    [mode, replyToMessage]
  )

  const { control, handleSubmit, setValue, register } =
    useForm<ComposeFormValues>({
      defaultValues: {
        to: defaultTo,
        cc: defaultCc,
        bcc: [],
        from_id: 0,
        subject: defaultSubject,
        body: defaultBody,
      },
      resolver: zodResolver(composeSchema),
      mode: 'onSubmit',
    })

  // In inline reply/reply_all mode the To field isn't rendered (a compact
  // header is shown instead), so register it manually for validation.
  // Also register cc for reply_all so default Cc values are submitted.
  useEffect(() => {
    if (inline && mode !== 'forward') {
      register('to')
      setValue('to', defaultTo)
    }
    if (inline && mode === 'reply_all' && defaultCc.length > 0) {
      register('cc')
      setValue('cc', defaultCc)
    }
  }, [inline, mode, defaultTo, defaultCc, register, setValue])

  // Select the email integration matching the thread's integration email,
  // falling back to the first available integration
  useEffect(() => {
    if (!emailIntegrationOptions?.length) return
    const match = integrationEmail
      ? emailIntegrationOptions.find(
          (opt) => opt.label.toLowerCase() === integrationEmail.toLowerCase()
        )
      : undefined
    setValue('from_id', match?.value ?? emailIntegrationOptions[0].value)
  }, [emailIntegrationOptions, integrationEmail, setValue])

  // Recipient search with debounce for server-side filtering
  const [recipientSearch, setRecipientSearch] = useState('')
  const [debouncedSearch] = useDebounce(recipientSearch, 300)

  // Team users (loaded once, filtered client-side)
  const { data: users } = api.useGetUsers(false)

  // Contact suggestions from email threads (server-side search)
  const { data: senderContacts, isLoading: loadingSenders } = useQuery({
    queryKey: ['distinct-senders', debouncedSearch],
    queryFn: () =>
      api.emailIntegration.getDistinctSenders({ search: debouncedSearch }),
  })
  const { data: recipientContacts, isLoading: loadingRecipients } = useQuery({
    queryKey: ['distinct-recipients', debouncedSearch],
    queryFn: () =>
      api.emailIntegration.getDistinctRecipients({ search: debouncedSearch }),
  })

  const isLoadingRecipients =
    recipientSearch !== debouncedSearch || loadingSenders || loadingRecipients

  // Merge team users + email contacts, deduplicated by email
  const recipientOptions = useMemo(() => {
    const seen = new Set<string>()
    const options: { label: string; value: string }[] = []

    const addOption = (email: string, name: string) => {
      const key = email.toLowerCase()
      if (seen.has(key)) return
      seen.add(key)
      options.push({
        label: name ? `${name} <${email}>` : email,
        value: email,
      })
    }

    // Team users first (always relevant for internal forwarding)
    users
      ?.filter((u) => u.is_active && u.email)
      .forEach((u) =>
        addOption(u.email, `${u.first_name} ${u.last_name}`.trim())
      )

    // Contact suggestions from email history
    senderContacts?.forEach((c) => addOption(c.email, c.name))
    recipientContacts?.forEach((c) => addOption(c.email, c.name))

    // Thread-context contacts (always include for reply scenarios)
    replyToMessage?.sender?.forEach((c) => addOption(c.email, c.name))
    replyToMessage?.to?.forEach((c) => addOption(c.email, c.name))
    replyToMessage?.cc?.forEach((c) => addOption(c.email, c.name))

    return options
  }, [users, senderContacts, recipientContacts, replyToMessage])

  const handleRemoveAttachment = createRemoveHandler((index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index))
  })

  /** Shared props for To/Cc/Bcc recipient selects. */
  const recipientSelectProps = useMemo(
    () => ({
      placeholder: 'Search contacts or team members...',
      isMulti: true as const,
      options: recipientOptions,
      onSearchChange: setRecipientSearch,
      isLoading: isLoadingRecipients,
      className: 'mt-0',
      onCreateOption: createEmailOption,
    }),
    [recipientOptions, isLoadingRecipients]
  )

  /** Resolve a display name from recipientOptions for a given email. */
  const resolveContactName = (email: string): string => {
    const opt = recipientOptions.find((o) => o.value === email)
    if (!opt?.label?.includes('<')) return ''
    return opt.label.split('<')[0].trim()
  }

  /** Map an array of email strings to {name, email} contact objects. */
  const buildRecipients = (emails: string[]) =>
    emails.map((email) => ({ name: resolveContactName(email), email }))

  const onSubmit = handleSubmit((values) => {
    // Prevent sending with uninitialized from_id
    if (!values.from_id) return

    const toRecipients = buildRecipients(values.to)
    const ccRecipients = values.cc?.length ? buildRecipients(values.cc) : null
    const bccRecipients = values.bcc?.length
      ? buildRecipients(values.bcc)
      : null

    // Build FormData for the API call
    const formData = new FormData()
    formData.append('to', JSON.stringify(toRecipients))
    formData.append('subject', values.subject)
    formData.append('body', values.body)

    if (
      (mode === 'reply' || mode === 'reply_all') &&
      replyToMessage?.email_id
    ) {
      formData.append('reply_to_message_id', replyToMessage.email_id)
    }

    if (ccRecipients) formData.append('cc', JSON.stringify(ccRecipients))
    if (bccRecipients) formData.append('bcc', JSON.stringify(bccRecipients))

    attachments.forEach((attachment) => {
      if (isNewAttachment(attachment)) {
        formData.append('attachments', attachment.file)
      }
    })

    // Build optimistic message from form values
    const optimisticMessage: EmailMessage = {
      id: -Date.now(),
      outbound: true,
      related_contacts: [],
      sender: [
        {
          name: `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim(),
          email: integrationEmail || '',
        },
      ],
      to: toRecipients,
      cc: ccRecipients,
      subject: values.subject,
      snippet: values.body.replace(/<[^>]*>/g, '').slice(0, 100),
      date: new Date().toISOString(),
      body: values.body,
      attachments: [],
    }

    onSend({ optimisticMessage, formData, fromId: values.from_id })
  })

  /** Summarize who the reply is addressed to for the compact inline header. */
  const inlineRecipientLabel = useMemo(() => {
    if (!defaultTo.length) return ''
    // Resolve a display label from the first To recipient
    const email = defaultTo[0]
    const opt = recipientOptions.find((o) => o.value === email)
    const label = opt?.label || email
    if (defaultTo.length > 1) return `${label} (+${defaultTo.length - 1})`
    return label
  }, [defaultTo, recipientOptions])

  const userInitials =
    [user?.first_name, user?.last_name]
      .filter(Boolean)
      .map((n) => n![0].toUpperCase())
      .join('')
      .slice(0, 2) || 'UN'

  const sendDisabled = !emailIntegrationOptions?.length
  const sendButton = (
    <button
      type="button"
      disabled={sendDisabled}
      onClick={() => onSubmit()}
      className={cn(
        'h-8 px-3.5 rounded-md text-sm font-medium text-white whitespace-nowrap',
        sendDisabled
          ? 'bg-surface-disabled cursor-not-allowed'
          : 'bg-brand hover:bg-brand-hover active:bg-brand-pressed'
      )}
    >
      Send
    </button>
  )

  const toolbarRight = (
    <div className="flex items-center gap-1.5">
      {dealIdentifier && (
        <DealBadge dealIdentifier={dealIdentifier} dealName={dealName} />
      )}
      {!inline && companyName && (
        <span className={CATEGORY_BADGE_CLASS}>{companyName}</span>
      )}
      <button
        type="button"
        onClick={onDiscard}
        className="text-xs font-medium text-fg-secondary underline decoration-solid hover:text-fg whitespace-nowrap"
      >
        Cancel
      </button>
    </div>
  )

  const ccBccToggles =
    !showCc || !showBcc ? (
      <div className="flex items-center gap-1 my-2">
        {!showCc && (
          <button
            type="button"
            onClick={() => setShowCc(true)}
            className="text-xs text-fg-secondary hover:text-fg hover:underline"
          >
            Cc
          </button>
        )}
        {!showBcc && (
          <button
            type="button"
            onClick={() => setShowBcc(true)}
            className="text-xs text-fg-secondary hover:text-fg hover:underline"
          >
            Bcc
          </button>
        )}
      </div>
    ) : null

  /** Shared compose fields (To/Cc/Bcc/Subject/Body/Attachments). */
  const composeFields = (
    <>
      {/* Compact inline header for reply/reply_all (not forward) */}
      {inline && mode !== 'forward' && (
        <div className="flex items-center gap-2 px-3 py-2 border-b border-line-subtle">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="p-1 rounded hover:bg-surface-alt text-fg-secondary hover:text-fg transition-colors flex-shrink-0"
              >
                <PiArrowBendUpLeft className="size-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" style={{ zIndex }}>
              <DropdownMenuItem onSelect={() => onModeChange?.('reply')}>
                Reply
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onModeChange?.('reply_all')}>
                Reply All
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onModeChange?.('forward')}>
                Forward
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <span className="text-sm font-medium text-fg truncate">
            {inlineRecipientLabel}
          </span>
          {ccBccToggles}
        </div>
      )}

      <div className={cn(inline ? 'p-3' : '')}>
        {/* To field for forward and standalone compose */}
        {(mode === 'forward' || !inline) && (
          <>
            {standalone ? (
              <div className="mb-2">
                <RecipientSelect
                  label={undefined}
                  control={control}
                  name="to"
                  {...recipientSelectProps}
                  placeholder="To"
                />
              </div>
            ) : (
              <RecipientSelect
                label="To"
                control={control}
                name="to"
                {...recipientSelectProps}
              />
            )}
            {!standalone && ccBccToggles}
          </>
        )}

        {/* Cc field */}
        {showCc && (
          <div className={standalone ? 'mb-2' : undefined}>
            <RecipientSelect
              label={standalone ? undefined : 'Cc'}
              control={control}
              name="cc"
              {...recipientSelectProps}
              placeholder={standalone ? 'Cc' : recipientSelectProps.placeholder}
            />
          </div>
        )}

        {/* Bcc field */}
        {showBcc && (
          <div className="mb-2">
            <RecipientSelect
              label={standalone ? undefined : 'Bcc'}
              control={control}
              name="bcc"
              {...recipientSelectProps}
              placeholder={
                standalone ? 'Bcc' : recipientSelectProps.placeholder
              }
            />
          </div>
        )}

        {/* Cc/Bcc toggles — always below all recipient fields in standalone */}
        {standalone && (!showCc || !showBcc) && (
          <div className="flex justify-end gap-1.5 mb-2">
            {!showCc && (
              <button
                type="button"
                onClick={() => setShowCc(true)}
                className="text-xs text-fg-secondary hover:text-fg hover:underline"
              >
                Cc
              </button>
            )}
            {!showBcc && (
              <button
                type="button"
                onClick={() => setShowBcc(true)}
                className="text-xs text-fg-secondary hover:text-fg hover:underline"
              >
                Bcc
              </button>
            )}
          </div>
        )}

        {/* Subject — only for standalone compose and forward */}
        {!inline && (
          <>
            <div className={standalone ? 'mb-2' : undefined}>
              <TextInput
                control={control}
                name="subject"
                type="text"
                label={standalone ? undefined : 'Subject'}
                placeholder="Subject"
                noMargin
                className="[&_label]:text-fg-muted"
              />
            </div>
            {standalone && <hr className="border-line-subtle m-0" />}
          </>
        )}

        {/* Body editor */}
        <div
          className={cn(
            inline ? 'mt-0' : 'mt-2',
            standalone &&
              'flex-1 min-h-0 flex flex-col [&>div]:flex-1 [&>div]:flex [&>div]:flex-col [&>div>div]:flex-1 [&>div>div]:flex [&>div>div]:flex-col'
          )}
        >
          <StyledEditorRHF
            name="body"
            borderless={inline}
            className={cn(
              'min-h-28 [&_.ql-container]:min-h-28 [&_.ql-editor]:min-h-28',
              inline && '[&_.ql-editor]:!py-2 [&_.ql-editor]:!px-2',
              standalone &&
                'flex-1 [&_.ql-container]:flex-1 [&_.ql-editor]:min-h-0 [&_.ql-toolbar]:!border-0 [&_.ql-container]:!border-0'
            )}
            control={control}
            placeholder={inline ? '' : 'Write your message...'}
            toolbarPrefix={sendButton}
            toolbarSuffix={toolbarRight}
            additionalToolbarButtons={[
              <CgAttachment
                key="attachments"
                size={24}
                className="p-1 w-8 cursor-pointer hover:text-brand"
                onClick={openFileSelect}
              />,
            ]}
          />
        </div>

        {/* Attachments */}
        <AttachmentList
          attachments={attachments}
          onRemove={handleRemoveAttachment}
          maxSize={MAX_EMAIL_ATTACHMENT_SIZE}
          warningThreshold={EMAIL_ATTACHMENT_WARNING_THRESHOLD}
        />
      </div>
    </>
  )

  return (
    <>
      {inline ? (
        /* Gmail-style: avatar on left, compose card on right */
        <div className="border-t border-line-subtle px-6 py-4">
          <div className="flex items-start gap-3">
            {/* User avatar — aligned with ThreadMessage sender avatars */}
            <div className="w-10 h-10 rounded-full bg-fg-muted flex items-center justify-center flex-shrink-0 mt-1">
              <span className="text-white font-bold text-sm">
                {userInitials}
              </span>
            </div>
            {/* Compose card */}
            <div className="flex-1 rounded-xl border border-line-subtle shadow-[0px_2px_6px_rgba(0,0,0,0.15),0px_1px_2px_rgba(0,0,0,0.3)] bg-surface overflow-y-auto max-h-[50vh]">
              {composeFields}
            </div>
          </div>
        </div>
      ) : (
        <div
          className={cn(
            standalone
              ? 'flex flex-col flex-1 min-h-0 px-4 pt-7 pb-4'
              : // Embedded path (mode="compose", no standalone prop): currently has no
                // callers — both active callers pass standalone. Parent is expected
                // to provide container chrome (border/shadow/padding) if this is ever used.
                'p-4 mt-3 bg-surface'
          )}
        >
          {composeFields}
        </div>
      )}

      <input
        type="file"
        hidden
        multiple
        ref={fileInputRef}
        onChange={(e) => handleFileSelect(e, attachments)}
        accept={getAcceptString(ALLOWED_EMAIL_ATTACHMENT_TYPES)}
      />
    </>
  )
}
